import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { History, Trash2 } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { CUSTOMER_NAV } from "@/lib/nav";
import { listRecentlyViewed, clearRecentlyViewed, type RecentItem } from "@/lib/recently-viewed";

export const Route = createFileRoute("/_authenticated/customer/recently-viewed")({
  head: () => ({
    meta: [
      { title: "Recently Viewed · Landoo" },
      { name: "description", content: "Listings you have recently opened on Landoo." },
    ],
  }),
  component: RecentlyViewedPage,
});

function RecentlyViewedPage() {
  const [items, setItems] = useState<RecentItem[]>([]);
  useEffect(() => setItems(listRecentlyViewed()), []);

  const clear = () => {
    clearRecentlyViewed();
    setItems([]);
  };

  return (
    <DashboardShell title="Recently Viewed" role="Customer" items={CUSTOMER_NAV}>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          The last {items.length} listing{items.length === 1 ? "" : "s"} you opened.
        </p>
        {items.length > 0 && (
          <button
            onClick={clear}
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-1.5 text-xs font-medium text-brown-dark hover:bg-cream"
          >
            <Trash2 className="h-3.5 w-3.5" /> Clear history
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="grid place-items-center gap-3 rounded-3xl border border-dashed border-border bg-card p-10 text-center">
          <History className="h-8 w-8 text-muted-foreground" />
          <div className="text-sm font-medium text-brown-dark">No recent views yet</div>
          <p className="max-w-xs text-xs text-muted-foreground">
            Open any listing from the map or search results and it will appear here.
          </p>
          <Link
            to="/"
            className="mt-1 rounded-xl bg-coral px-4 py-2 text-xs font-semibold text-white shadow-soft"
          >
            Explore map
          </Link>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <Link
              key={it.id}
              to="/property/$id"
              params={{ id: it.id }}
              className="group overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="aspect-video w-full bg-cream">
                {it.image ? (
                  <img
                    src={it.image}
                    alt={it.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : null}
              </div>
              <div className="space-y-1 p-3">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {it.service && (
                    <span className="rounded-full bg-cream px-2 py-0.5 font-semibold text-brown-dark">
                      {it.service}
                    </span>
                  )}
                  <span>{new Date(it.viewedAt).toLocaleDateString()}</span>
                </div>
                <div className="line-clamp-1 text-sm font-semibold text-brown-dark">{it.title}</div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="line-clamp-1">{it.city ?? ""}</span>
                  {it.price && <span className="font-semibold text-coral">{it.price}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
